import React, { useState, useEffect, useCallback } from "react";
import * as Styled from "../../../CompanyForm.styled";
import * as MUIStyled from "../../../MUIStyled.js";
import { CustomTextField } from "../../elements/TextField/TextField";
import usePlacesAutocomplete from "use-places-autocomplete";
import useOnclickOutside from "react-cool-onclickoutside";

export const PlacesAutoComplete = ({
    tradingAddress,
    postalAddress,
    isTradingAddress,
    addressDisabled,
    onAddressChange,
    errorMessage,
    error,
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [dropPlacesAddresses, setDropPlacesAddresses] = useState([]);

    const [addressSelected, setAddressSelected] = useState("");
    const {
        ready,
        value,
        suggestions: { status, data },
        setValue,
        clearSuggestions,
    } = usePlacesAutocomplete({
        callbackName: "handleGooglePlacesAPICall",
        requestOptions: {
            componentRestrictions: { country: "au" },
        },
        debounce: 300,
    });

    const ref = useOnclickOutside(() => {
        clearSuggestions();
        setAnchorEl(null);
    });

    useEffect(() => {
        window.handleGooglePlacesAPICall = () => {};

        return () => {
            delete window.handleGooglePlacesAPICall;
        };
    }, []);

    useEffect(() => {
        if (tradingAddress !== undefined) {
            let fieldValue = "";
            if (tradingAddress.streetAddress) {
                fieldValue += `${tradingAddress.streetAddress}`;
            }
            if (tradingAddress.subPremise) {
                fieldValue += `, ${tradingAddress.subPremise}`;
            }
            if (tradingAddress.suburb) {
                fieldValue += `, ${tradingAddress.suburb}`;
            }
            if (tradingAddress.state) {
                if (!tradingAddress.suburb) {
                    fieldValue += `,`;
                }
                fieldValue += ` ${tradingAddress.state}`;
            }
            if (tradingAddress.postcode) {
                fieldValue += `, ${tradingAddress.postcode}`;
            }
            // trim extra spaces
            fieldValue = fieldValue.replace(/\s+/g, " ").trim();
            setAddressSelected(fieldValue);
        }

        if (postalAddress !== undefined) {
            let fieldValue = "";
            if (postalAddress.streetAddress) {
                fieldValue += `${postalAddress.streetAddress}`;
            }
            if (postalAddress.subPremise) {
                fieldValue += `, ${postalAddress.subPremise}`;
            }
            if (postalAddress.suburb) {
                fieldValue += `, ${postalAddress.suburb}`;
            }
            if (postalAddress.state) {
                if (!postalAddress.suburb) {
                    fieldValue += `,`;
                }
                fieldValue += ` ${postalAddress.state}`;
            }
            if (postalAddress.postcode) {
                fieldValue += `, ${postalAddress.postcode}`;
            }
            fieldValue = fieldValue.replace(/\s+/g, " ").trim();
            setAddressSelected(fieldValue);
        }
    }, [tradingAddress, postalAddress]);

    useEffect(() => {
        if (status === "OK") {
            setDropPlacesAddresses(data);
        }
    }, [status, data]);

    const setFieldLabelByAddressType = useCallback(() => {
        return isTradingAddress ? "Trading Address *" : "Postal Address *";
    }, [isTradingAddress]);

    const handleInput = useCallback(
        (e) => {
            setValue(e.target.value);
            setAddressSelected("");
            if (e.target.value) {
                setAnchorEl(e.currentTarget);
            } else {
                setAnchorEl(null);
            }

            // send to parent
            onAddressChange(isTradingAddress, "", "");
        },
        [setValue, setAnchorEl, onAddressChange, isTradingAddress]
    );

    const handleSelect = (suggestion) => {
        const selectedAddress = suggestion.description;
        setAddressSelected(selectedAddress);
        setValue(selectedAddress); // Update input field with the selected address
        setAnchorEl(null); // Close the popover
        clearSuggestions();

        // send to parent
        onAddressChange(isTradingAddress, selectedAddress, suggestion.place_id);
    };

    return (
        <Styled.AutoPlaces ref={ref}>
            <CustomTextField
                label={setFieldLabelByAddressType()}
                fullWidth
                variant="outlined"
                type="text"
                value={addressSelected || value}
                onChange={handleInput}
                disabled={!ready || addressDisabled}
                error={error} // Pass error prop to indicate if there's an error
                helperText={error ? errorMessage : ""}
                sx={{
                    marginBottom: "20px",
                    backgroundColor: addressDisabled
                        ? "#ecececff"
                        : "transparent",
                    width: "100%",
                }}
            />

            <MUIStyled.CustomPopover
                open={Boolean(anchorEl) && status === "OK"}
                anchorEl={anchorEl}
                onClose={() => {
                    clearSuggestions();
                    setAnchorEl(null);
                }}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                }}
                PaperProps={{
                    style: { width: anchorEl?.offsetWidth },
                }}
                disableAutoFocus>
                <MUIStyled.CustomBox sx={{ padding: 1, width: "100%" }}>
                    {dropPlacesAddresses.map((suggestion) => {
                        const {
                            place_id,
                            structured_formatting: {
                                main_text,
                                secondary_text,
                            },
                        } = suggestion;

                        return (
                            <MUIStyled.CustomMenuItem
                                key={place_id}
                                onClick={() => handleSelect(suggestion)}
                                sx={{ padding: 1, width: "100%" }}>
                                <Styled.TradingDropdownAddress1>
                                    {main_text}
                                    {","}&nbsp;
                                </Styled.TradingDropdownAddress1>
                                <Styled.TradingDropdownAddress2>
                                    {secondary_text}
                                </Styled.TradingDropdownAddress2>
                            </MUIStyled.CustomMenuItem>
                        );
                    })}
                    <Styled.TradingPlacesAutocomplete>
                        Powered by <span style={{ color: "#EA4335" }}>G</span>
                        <span style={{ color: "#FBBC05" }}>o</span>
                        <span style={{ color: "#34A853" }}>o</span>
                        <span style={{ color: "#4285F4" }}>g</span>
                        <span style={{ color: "#EA4335" }}>l</span>
                        <span style={{ color: "#FBBC05" }}>e</span>
                    </Styled.TradingPlacesAutocomplete>
                </MUIStyled.CustomBox>
            </MUIStyled.CustomPopover>
        </Styled.AutoPlaces>
    );
};
